import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import ClientInterviewView from './ClientInterviewView';
import CustomerPortal from './CustomerPortal';
import OnboardingInformation from './OnboardingInformation';
import CompanyPageTab from './CompanyPageTab';
import './ClientPortal.css';

function ClientPortal({ user, onLogout }) {
  // Mobile lands on Ready for Interview (the higher-intent, action-needed tab); desktop keeps
  // the existing Driver Candidates default. window.innerWidth is safe to read here -- this only
  // runs once, in the initializer, before any resize listener would matter.
  const [activeTab, setActiveTab] = useState(() => window.innerWidth <= 768 ? 'interview' : 'candidates');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', communication_preference: 'Email', password: '' });
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [interviewBadge, setInterviewBadge] = useState(0);
  // Books hold (October 2026): an invoice 7+ days overdue; candidates sent since are held.
  const [holds, setHolds] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/customer-hold`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setHolds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setHolds([]));
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/candidates/interviews/badge-count`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setInterviewBadge(r.data.count || 0)).catch(() => {});
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/users/team`, newMember, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMember({ name: '', email: '', password: '' });
      setShowAddForm(false);
      fetchTeamMembers();
      showToast('Team member added successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding team member');
    }
  };

  const openTeamModal = () => {
    setShowTeamModal(true);
    fetchTeamMembers();
  };

  return (
    <div className="client-portal">
      <header className="client-header">
        <div className="header-left">
          <img src="/logo.png" alt="Mountain Recruiting" className="logo" />
        </div>
        <div className="header-right">
          <span className="user-info">{user.name || user.email}</span>
          <button onClick={openTeamModal} className="team-btn">Manage Team</button>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      {holds.map(h => (
        <div className="cp-hold" key={h.invoice}>
          <div>
            <b>{h.held_count ? `${h.held_count} new candidate${h.held_count === 1 ? ' is' : 's are'} ready for you.` : 'New candidates are on hold.'}</b>{' '}
            Pay invoice {h.invoice} (${Number(h.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}) to see who they are. They'll appear here the moment your payment comes through.
            <span className="cp-hold-sub">Already paid? Thank you! Email hiring@mountainrecruiting.com and we'll unlock them today.</span>
          </div>
          {h.link && <a className="cp-hold-pay" href={h.link} target="_blank" rel="noopener noreferrer">Pay invoice</a>}
        </div>
      ))}

      <div className="client-tabs">
        <button
          className={`tab-btn ${activeTab === 'interview' ? 'active' : ''}`}
          onClick={() => setActiveTab('interview')}
        >
          Ready for Interview
          {interviewBadge > 0 && (
            <span style={{
              marginLeft: 7, background: 'var(--red)', color: 'white',
              borderRadius: 10, fontSize: 11, fontWeight: 700,
              padding: '1px 7px', verticalAlign: 'middle', lineHeight: '18px',
              display: 'inline-block',
            }}>{interviewBadge}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'candidates' ? 'active' : ''}`}
          onClick={() => setActiveTab('candidates')}
        >
          Driver Candidates
        </button>
        <button
          className={`tab-btn ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding Information
        </button>
        <button
          className={`tab-btn ${activeTab === 'company-page' ? 'active' : ''}`}
          onClick={() => setActiveTab('company-page')}
        >
          Company Page
        </button>
      </div>

      <div className="client-content">
        {activeTab === 'interview' ? (
          <ClientInterviewView user={user} onLogout={onLogout} hideHeader={true} />
        ) : activeTab === 'candidates' ? (
          <CustomerPortal user={user} onLogout={onLogout} hideHeader={true} />
        ) : activeTab === 'onboarding' ? (
          <OnboardingInformation user={user} onLogout={onLogout} hideHeader={true} />
        ) : (
          <CompanyPageTab user={user} />
        )}
      </div>

      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div className="modal-content team-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Team Management</h3>
            <p className="modal-subtitle">Manage users from your organization</p>

            {!showAddForm ? (
              <>
                <div className="team-list">
                  {teamMembers.length === 0 ? (
                    <p className="empty-message">No team members yet</p>
                  ) : (
                    <table className="team-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Comm. Pref.</th>
                          <th>Added On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map(member => (
                          <tr key={member.id}>
                            <td>{member.name || '-'}</td>
                            <td>{member.email}</td>
                            <td>{member.phone || '-'}</td>
                            <td>{member.communication_preference || 'Email'}</td>
                            <td>{new Date(member.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-actions">
                  <button onClick={() => setShowAddForm(true)} className="btn-add-member">
                    + Add Team Member
                  </button>
                  <button onClick={() => setShowTeamModal(false)} className="btn-close">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleAddTeamMember} className="add-member-form">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    required
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    required
                    placeholder="john@company.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number (with country code)</label>
                  <input
                    type="tel"
                    value={newMember.phone}
                    onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                    placeholder="+1234567890"
                    pattern="^\+[1-9]\d{1,14}$"
                    title="Please enter phone number with country code (e.g., +1234567890)"
                  />
                </div>
                <div className="form-group">
                  <label>Communication Preference *</label>
                  <select
                    value={newMember.communication_preference}
                    onChange={(e) => setNewMember({ ...newMember, communication_preference: e.target.value })}
                    required
                  >
                    <option value="Email">Email</option>
                    <option value="Text">Text</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    required
                    minLength="6"
                    placeholder="Minimum 6 characters"
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowAddForm(false); setError(''); }} className="btn-cancel">
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    Add Member
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className={`cp-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default ClientPortal;
